// src/pages/Library.jsx
import { useEffect, useMemo, useState, useLayoutEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BookCard from '../components/books/BookCard';
import BookCardModal from '../components/books/BookCardModal';
import DashboardCard from '../components/dashboard/DashboardCard';
import ManuscriptsWorkspace from '../components/manuscripts/ManuscriptsWorkspace';
import TemplateGallery from './TemplateGallery';
import SidebarLibrary from '../components/library/SidebarLibrary';
import CoverCard from '../components/covers/CoverCard';
import EditorCoverCard from '../components/editor/CoverCard';
import CoverCardModal from '../components/covers/CoverCardModal';
import LinkModal from '../components/library/LinkModal';
import { API_BASE } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { tokens } from '../theme';
import '../styles/EditorHome.css';

// Helper functions from AuthorPanel
const computePdfUrl = (b, status) => {
  if (!b) return '';
  if (b.pdfUrl) return b.pdfUrl;
  const base = `${API_BASE}/api/drafts/${b._id}/pdf`;
  const q = new URLSearchParams();
  if (status && status !== 'pending_review') q.set('preview', '1');
  const qs = q.toString();
  return qs ? `${base}?${qs}` : base;
};

const COVER_BASE = `${API_BASE}/static/images/book-covers`;
const NOT_FOUND_COVER = `${COVER_BASE}/notFound.jpg`;
const computeCoverUrl = (b) => {
  // Prefer the backend's normalized URL; a raw coverImage is an S3 key that no
  // host serves as a static path, so it must come last.
  const v = b?.coverUrl || b?.cover_url || b?.coverImage || '';
  if (!v) return NOT_FOUND_COVER;
  if (v.startsWith('http')) return v;
  if (v.startsWith('/')) return `${API_BASE}${v}`;
  return `${COVER_BASE}/${v}`;
};

const mapToLibraryBook = (item, me) => {
  const id = item._id || item.id;
  const cover = item.cover_url || computeCoverUrl(item);
  return {
    id,
    _id: id,
    title: item.title || 'Untitled',
    authorName: item.authorName || me?.name || 'You',
    genre: item.genre || '',
    volume: item.volume || '',
    blurb: item.blurb || '',
    coverUrl: cover,
    coverImage: cover,
  };
};

export default function Library() {
  const navigate = useNavigate();
  const location = useLocation();
  // From the auth context, NOT from localStorage.
  //
  // This page used to read the `user` key straight out of localStorage, once,
  // in a `useMemo` with an empty dependency array -- and every loader on the
  // page is gated behind `if (user?.id && accessToken)`. AuthContext writes
  // that key only after GET /api/me comes back, which is after Library has
  // mounted. So on any session where the key was not already there -- a new
  // sign-in, or anyone who has cleared their site data -- the page mounted
  // with `user` null, never recomputed it, and sat on a spinner forever
  // without ever requesting a single draft. /api/me returned 200 throughout,
  // so nothing anywhere looked wrong.
  //
  // The context value is React state, so it arrives when auth settles and the
  // effects re-run on their own.
  const { user } = useAuth();
  const currentUserId = user?.id;

  // scroll refs for My Books sections
  const approvedScrollRef       = useRef(null);
  const pendingScrollRef        = useRef(null);
  const publishedScrollRef      = useRef(null);

  const scrollContainer = (ref, direction) => {
    if (!ref.current) return;
    const amount = direction === 'left' ? -300 : 300;
    ref.current.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const [books, setBooks] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sizes, setSizes] = useState({ top: 56, bottom: 56 });
  // A previewed template is in the URL (`?template=<id>`), so a link or a reload
  // lands back on the Templates tab with that template open.
  const [viewMode, setViewMode] = useState(
    location.state?.initialView
      || (new URLSearchParams(location.search).has('template') ? 'templates' : 'purchased')
  ); // 'library', 'myBooks', 'drafts', 'purchased', 'wishlist', 'coverMarket', 'myCovers', 'templates'

  // Leaving the Templates tab drops its `?template=` so a reload does not reopen it.
  const switchView = (view) => {
    setViewMode(view);
    if (view !== 'templates' && new URLSearchParams(location.search).has('template')) {
      navigate(location.pathname, { replace: true, state: location.state });
    }
  };

  // My Books data (from AuthorPanel)
  const [draftBooks, setDraftBooks] = useState([]);
  const [approvedBooks, setApprovedBooks] = useState([]);
  const [pendingBooks, setPendingBooks] = useState([]);
  const [publishedBooks, setPublishedBooks] = useState([]);
  const [ownedBooks, setOwnedBooks] = useState([]);
  const [wishlistBooks, setWishlistBooks] = useState([]);

  // My Books search + filter
  const [myBooksSearch, setMyBooksSearch] = useState('');
  const MY_BOOKS_FILTERS = ['All', 'Approved', 'Pending', 'Published'];
  const [myBooksFilter, setMyBooksFilter] = useState('All');
  const [purchasedSearch, setPurchasedSearch] = useState('');
  const [purchasedStatus, setPurchasedStatus] = useState('All');
  const purchasedStatuses = ['All', 'Reading', 'Completed', 'Unread'];

  const [rejectedDraftsCount, setRejectedDraftsCount] = useState(0);

  // Cover Marketplace state
  const [marketCovers, setMarketCovers] = useState([]);
  const [filteredCovers, setFilteredCovers] = useState([]);
  const [coverSearch, setCoverSearch] = useState('');
  const [activeCoverType, setActiveCoverType] = useState('All');
  const [modalCover, setModalCover] = useState(null);
  const [myCovers, setMyCovers] = useState([]);
  const [coverDrafts, setCoverDrafts] = useState([]);
  const [manuscriptCovers, setManuscriptCovers] = useState([]);
  const [pendingCovers, setPendingCovers] = useState([]);
  const [listedCovers, setListedCovers] = useState([]);
  const [assignedCovers, setAssignedCovers] = useState([]);

  const accessToken = localStorage.getItem('rv_access_token');

  const [search, setSearch] = useState('');
  const [activeGenre, setActiveGenre] = useState('All');
  const [modalBook, setModalBook] = useState(null);

  // Link modal state
  // mode: 'cover' (picking a cover for a manuscript) | 'manuscript' (picking a manuscript for a cover)
  const [linkModal, setLinkModal] = useState({ open: false, mode: 'cover', draftId: null, currentLinkedId: null });

  // Delete confirmation dialog
  // scenario: 'manuscript_cover' | 'draft_with_linked_cover' | 'draft_no_cover' | 'cover_only_draft'
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, draftId: null, scenario: null, linkedCoverId: null });

  useLayoutEffect(() => {
    const header = document.querySelector('.global-header') || document.querySelector('header');
    const footer = document.querySelector('.global-footer') || document.querySelector('footer');
    setSizes({
      top: header ? header.getBoundingClientRect().height : 56,
      bottom: footer ? footer.getBoundingClientRect().height : 56,
    });
  }, []);

  // rejectedDraftsCount is computed inside each drafts-fetching effect — no separate fetch needed

  // Load public library books
  useEffect(() => {
    if (viewMode !== 'library') return;
    
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
        const res = await fetch(`${API_BASE}/api/library`, {
          credentials: 'omit',
          headers,
        });
        if (!res.ok) throw new Error('Failed to fetch library');
        const data = await res.json();
        if (!alive) return;
        const list = Array.isArray(data?.books) ? data.books : Array.isArray(data) ? data : [];
        setBooks(list);
        setFiltered(list);
      } catch (e) {
        if (alive) {
          setError('Could not load the library.');
          setBooks([]);
          setFiltered([]);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [accessToken, viewMode]);

  // Load My Books data (approved, pending, published — no drafts)
  useEffect(() => {
    if (viewMode !== 'myBooks') return;

    const load = async () => {
      setLoading(true);
      try {
        const headers = {
          Accept: 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        };

        const draftsRes = await fetch(`${API_BASE}/api/drafts`, { credentials: 'omit', headers });
        const draftsJson = draftsRes.ok ? await draftsRes.json().catch(() => []) : [];
        const drafts = Array.isArray(draftsJson) ? draftsJson : [];

        setApprovedBooks(drafts.filter(b => b.status === 'approved'));
        setPendingBooks(drafts.filter(b => b.status === 'pending_review'));
        setRejectedDraftsCount(drafts.filter(b => b.status === 'draft' && b.type !== 'cover_only' && b.reviewNotes).length);

        const pubsRes = await fetch(`${API_BASE}/api/author/published`, { credentials: 'omit', headers });
        const pubsJson = pubsRes.ok ? await pubsRes.json().catch(() => []) : [];
        setPublishedBooks(Array.isArray(pubsJson) ? pubsJson : []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    if (user?.id && accessToken) load();
  }, [viewMode, user?.id, accessToken]);

  // Load Drafts (when viewMode is 'drafts')
  useEffect(() => {
    if (viewMode !== 'drafts') return;

    const load = async () => {
      setLoading(true);
      try {
        const headers = {
          Accept: 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        };

        const draftsRes = await fetch(`${API_BASE}/api/drafts`, { credentials: 'omit', headers });
        const draftsJson = draftsRes.ok ? await draftsRes.json().catch(() => []) : [];
        const drafts = Array.isArray(draftsJson) ? draftsJson : [];
        const filtered = drafts.filter(b => b.status === 'draft' && b.type !== 'cover_only');
        setDraftBooks(filtered);
        setRejectedDraftsCount(filtered.filter(b => b.reviewNotes).length);

      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    if (user?.id && accessToken) load();
  }, [viewMode, user?.id, accessToken]);

  // Load Purchased Books (when viewMode is 'purchased')
  useEffect(() => {
    if (viewMode !== 'purchased') return;
    
    const load = async () => {
      setLoading(true);
      try {
        const headers = {
          Accept: 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        };

        const ownedRes = await fetch(`${API_BASE}/api/my/library`, { credentials: 'omit', headers });
        const ownedJson = ownedRes.ok ? await ownedRes.json().catch(() => []) : [];
        setOwnedBooks(Array.isArray(ownedJson) ? ownedJson : []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    
    if (user?.id && accessToken) load();
  }, [viewMode, user?.id, accessToken]);

  // Load Cover Market (when viewMode is 'coverMarket')
  useEffect(() => {
    if (viewMode !== 'coverMarket') return;

    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
        const res = await fetch(`${API_BASE}/api/cover-market`, {
          credentials: 'omit',
          headers,
        });
        if (!res.ok) throw new Error('Failed to fetch covers');
        const data = await res.json();
        if (!alive) return;
        const list = Array.isArray(data) ? data : [];
        setMarketCovers(list);
        setFilteredCovers(list);
      } catch (e) {
        if (alive) {
          setMarketCovers([]);
          setFilteredCovers([]);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [accessToken, viewMode]);

  // Load My Covers (when viewMode is 'myCovers')
  useEffect(() => {
    if (viewMode !== 'myCovers') return;
    if (!user?.id || !accessToken) return;

    let alive = true;
    const headers = {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    };

    const load = async () => {
      setLoading(true);
      try {
        // Fetch drafts first (slowest call) then fire the rest in parallel
        const [draftsRes, purchRes, listRes] = await Promise.all([
          fetch(`${API_BASE}/api/drafts`, { credentials: 'omit', headers }),
          fetch(`${API_BASE}/api/my/covers`, { credentials: 'omit', headers }),
          fetch(`${API_BASE}/api/cover-market/my-listings`, { credentials: 'omit', headers }),
        ]);

        if (!alive) return;

        const draftsData = draftsRes.ok ? await draftsRes.json().catch(() => []) : [];
        const allDrafts = Array.isArray(draftsData) ? draftsData : [];

        const coverOnlyDrafts = allDrafts.filter(d => d.type === 'cover_only');
        setCoverDrafts(coverOnlyDrafts.filter(d => d.status === 'draft'));
        setPendingCovers(coverOnlyDrafts.filter(d =>
          d.status === 'pending_review' || d.status === 'in_review'
        ));
        setManuscriptCovers(allDrafts.filter(d =>
          d.type !== 'cover_only' &&
          (
            (Array.isArray(d.coverVersions) && d.coverVersions.length > 0) ||
            d.activeCoverVersionId ||
            d.coverImage ||
            d.cover_url
          )
        ));
        // Compute rejected count from same payload — no extra fetch needed
        setRejectedDraftsCount(
          allDrafts.filter(b => b.status === 'draft' && b.type !== 'cover_only' && b.reviewNotes).length
        );

        const purchData = purchRes.ok ? await purchRes.json().catch(() => []) : [];
        if (alive) setMyCovers(Array.isArray(purchData) ? purchData : []);

        const listData = listRes.ok ? await listRes.json().catch(() => []) : [];
        if (alive) setListedCovers(Array.isArray(listData) ? listData : []);

        // collaborator-queue not yet implemented — skip to avoid 404
        setAssignedCovers([]);
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => { alive = false; };
  }, [viewMode, user?.id, accessToken]);

  // Filter covers by search + type
  const coverTypes = useMemo(() => {
    const set = new Set();
    marketCovers.forEach(c => {
      const t = (c.coverType || '').trim();
      if (t) set.add(t);
    });
    return ['All', ...Array.from(set)];
  }, [marketCovers]);

  useEffect(() => {
    if (viewMode !== 'coverMarket') return;
    const term = coverSearch.trim().toLowerCase();
    let list = marketCovers;
    if (term) {
      list = list.filter(c =>
        (c.title || '').toLowerCase().includes(term) ||
        (c.designerName || '').toLowerCase().includes(term)
      );
    }
    if (activeCoverType !== 'All') {
      list = list.filter(c => (c.coverType || '').toLowerCase() === activeCoverType.toLowerCase());
    }
    setFilteredCovers(list);
  }, [coverSearch, activeCoverType, marketCovers, viewMode]);

  // Load Wishlist Books (when viewMode is 'wishlist')
  useEffect(() => {
    if (viewMode !== 'wishlist') return;
    
    const load = async () => {
      setLoading(true);
      try {
        const headers = {
          Accept: 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        };

        const wishlistRes = await fetch(`${API_BASE}/api/wishlist`, { credentials: 'omit', headers });
        const wishlistJson = wishlistRes.ok ? await wishlistRes.json().catch(() => []) : [];
        setWishlistBooks(Array.isArray(wishlistJson) ? wishlistJson : []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    
    if (user?.id && accessToken) load();
  }, [viewMode, user?.id, accessToken]);

  const genres = useMemo(() => {
    const set = new Set();
    books.forEach(b => {
      const g = (b.genre || '').trim();
      if (g) set.add(g);
    });
    return ['All', ...Array.from(set)];
  }, [books]);

  useEffect(() => {
    const term = search.trim().toLowerCase();
    let list = books;
    if (term) {
      list = list.filter(b =>
        (b.title || '').toLowerCase().includes(term) ||
        (b.authorName || b.author || '').toLowerCase().includes(term)
      );
    }
    if (activeGenre !== 'All') {
      list = list.filter(b => (b.genre || '').toLowerCase() === activeGenre.toLowerCase());
    }
    setFiltered(list);
  }, [search, activeGenre, books]);

  const handleOpenReader = (book) => {
    const id = book?._id || book?.id;
    if (!id) return;
    window.location.assign(`/read/${id}?type=book&kind=book`);
  };

  const handleBuy = async (book) => {
    console.log('Buy clicked for:', book?._id || book?.id);
    alert('Purchase flow not implemented yet in this environment.');
  };

  const handleFinalize = async (draftId) => {
    try {
      const res = await fetch(`${API_BASE}/api/publish/${draftId}`, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          Accept: 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return alert(`Failed to publish. ${err?.error ? `(${err.error})` : ''}`);
      }
      const published = await res.json();
      setApprovedBooks(prev => prev.filter(b => b._id !== draftId));
      setPublishedBooks(prev => [published, ...prev]);
      alert('Book published!');
    } catch (e) {
      console.error(e);
      alert('Error publishing the book.');
    }
  };

  const closeDeleteConfirm = () =>
    setDeleteConfirm({ open: false, draftId: null, scenario: null, linkedCoverId: null });

  const handleDeleteDraft = (draft) => {
    setDeleteConfirm({
      open: true,
      draftId: draft._id,
      scenario: draft.linkedCoverId ? 'draft_with_linked_cover' : 'draft_no_cover',
      linkedCoverId: draft.linkedCoverId || null,
    });
  };

  // Dropped off a manuscript (collaborator unassigning themselves). The row
  // goes from the listing without a refetch; the sidebar badge recounts because
  // it is derived from the same list.
  const handleManuscriptRemoved = (draftId) => {
    setDraftBooks(prev => {
      const updated = prev.filter(d => d._id !== draftId);
      setRejectedDraftsCount(updated.filter(b => b.reviewNotes).length);
      return updated;
    });
  };

  const handleDeleteCoverDraft = (draftId) => {
    setDeleteConfirm({ open: true, draftId, scenario: 'cover_only_draft', linkedCoverId: null });
  };

  const handleDeleteManuscriptCover = (draftId) => {
    setDeleteConfirm({ open: true, draftId, scenario: 'manuscript_cover', linkedCoverId: null });
  };

  const deleteDraftFromServer = async (draftId) => {
    const res = await fetch(`${API_BASE}/api/drafts/${draftId}`, {
      method: 'DELETE',
      credentials: 'omit',
      headers: { Accept: 'application/json', Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to delete (${res.status})`);
    }
  };

  const deleteCoverOnlyFromServer = async (draftId) => {
    const res = await fetch(`${API_BASE}/api/drafts/${draftId}/cover`, {
      method: 'DELETE',
      credentials: 'omit',
      headers: { Accept: 'application/json', Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to delete cover (${res.status})`);
    }
  };

  // Delete cover data only — manuscript stays
  const handleConfirmCoverOnly = async () => {
    const { draftId } = deleteConfirm;
    closeDeleteConfirm();
    try {
      await deleteCoverOnlyFromServer(draftId);
      setManuscriptCovers(prev => prev.filter(d => d._id !== draftId));
    } catch (err) {
      alert(err.message);
    }
  };

  // Delete entire manuscript (and its embedded cover)
  const handleConfirmCoverAndManuscript = async () => {
    const { draftId } = deleteConfirm;
    closeDeleteConfirm();
    try {
      await deleteDraftFromServer(draftId);
      setManuscriptCovers(prev => prev.filter(d => d._id !== draftId));
      setDraftBooks(prev => {
        const updated = prev.filter(d => d._id !== draftId);
        setRejectedDraftsCount(updated.filter(b => b.reviewNotes).length);
        return updated;
      });
    } catch (err) {
      alert(err.message);
    }
  };

  // Delete manuscript only — linked standalone cover stays
  const handleConfirmManuscriptOnly = async () => {
    const { draftId } = deleteConfirm;
    closeDeleteConfirm();
    try {
      await deleteDraftFromServer(draftId);
      setDraftBooks(prev => {
        const updated = prev.filter(d => d._id !== draftId);
        setRejectedDraftsCount(updated.filter(b => b.reviewNotes).length);
        return updated;
      });
    } catch (err) {
      alert(err.message);
    }
  };

  // Delete manuscript AND its linked standalone cover draft
  const handleConfirmManuscriptAndLinkedCover = async () => {
    const { draftId, linkedCoverId } = deleteConfirm;
    closeDeleteConfirm();
    try {
      await deleteDraftFromServer(draftId);
      if (linkedCoverId) await deleteDraftFromServer(linkedCoverId).catch(() => {});
      setDraftBooks(prev => {
        const updated = prev.filter(d => d._id !== draftId);
        setRejectedDraftsCount(updated.filter(b => b.reviewNotes).length);
        return updated;
      });
      setCoverDrafts(prev => prev.filter(d => d._id !== linkedCoverId));
    } catch (err) {
      alert(err.message);
    }
  };

  // Simple delete for standalone cover_only drafts
  const handleConfirmCoverDraft = async () => {
    const { draftId } = deleteConfirm;
    closeDeleteConfirm();
    try {
      await deleteDraftFromServer(draftId);
      setCoverDrafts(prev => prev.filter(d => d._id !== draftId));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleOpenAssignedCover = (task) => {
    navigate(`/bookcover/${task._id}/designer/${task.versionId || ''}`);
  };

  // Open LinkModal from a manuscript card (pick a cover for it)
  const handleOpenLinkCover = (manuscriptId) => {
    const draft = draftBooks.find(d => d._id === manuscriptId);
    setLinkModal({
      open: true,
      mode: 'cover',
      draftId: manuscriptId,
      currentLinkedId: draft?.linkedCoverId || null,
    });
  };

  // Open LinkModal from a cover card (pick a manuscript for it)
  const handleOpenLinkManuscript = (coverId) => {
    const cover = coverDrafts.find(d => d._id === coverId);
    setLinkModal({
      open: true,
      mode: 'manuscript',
      draftId: coverId,
      currentLinkedId: cover?.linkedManuscriptId || null,
    });
  };

  const handleLinkConfirm = async (selectedId) => {
    const { draftId, mode, currentLinkedId } = linkModal;
    if (!accessToken || !draftId) return;

    const body = selectedId === null
      ? (mode === 'cover' ? { linkedCoverId: null } : { linkedManuscriptId: null })
      : (mode === 'cover' ? { linkedCoverId: selectedId } : { linkedManuscriptId: selectedId });

    try {
      const res = await fetch(`${API_BASE}/api/drafts/${draftId}/link`, {
        method: 'PATCH',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || `Failed to update link (${res.status})`);
        return;
      }

      const data = await res.json();

      if (mode === 'cover') {
        // Update manuscript in draftBooks
        setDraftBooks(prev => prev.map(d => {
          if (d._id !== draftId) return d;
          return {
            ...d,
            linkedCoverId: data.linked ? data.coverId : null,
            linkedCoverTitle: data.linked ? (coverDrafts.find(c => c._id === data.coverId)?.title || 'Cover') : null,
          };
        }));
        // Update the cover in coverDrafts
        if (data.linked) {
          setCoverDrafts(prev => prev.map(c => {
            if (c._id !== data.coverId) return c;
            return { ...c, linkedManuscriptId: draftId, linkedManuscriptTitle: draftBooks.find(d => d._id === draftId)?.title || 'Manuscript' };
          }));
        } else if (currentLinkedId) {
          setCoverDrafts(prev => prev.map(c => {
            if (c._id !== currentLinkedId) return c;
            return { ...c, linkedManuscriptId: null, linkedManuscriptTitle: null };
          }));
        }
      } else {
        // Update cover in coverDrafts
        setCoverDrafts(prev => prev.map(c => {
          if (c._id !== draftId) return c;
          return {
            ...c,
            linkedManuscriptId: data.linked ? data.manuscriptId : null,
            linkedManuscriptTitle: data.linked ? (draftBooks.find(d => d._id === data.manuscriptId)?.title || 'Manuscript') : null,
          };
        }));
        // Update the manuscript in draftBooks
        if (data.linked) {
          setDraftBooks(prev => prev.map(d => {
            if (d._id !== data.manuscriptId) return d;
            return { ...d, linkedCoverId: draftId, linkedCoverTitle: coverDrafts.find(c => c._id === draftId)?.title || 'Cover' };
          }));
        } else if (currentLinkedId) {
          setDraftBooks(prev => prev.map(d => {
            if (d._id !== currentLinkedId) return d;
            return { ...d, linkedCoverId: null, linkedCoverTitle: null };
          }));
        }
      }
    } catch (err) {
      console.error('Link failed:', err);
      alert('Could not update the link. Please try again.');
    }
  };

  const handleUnlinkCover = async (manuscriptId) => {
    if (!accessToken) return;
    const draft = draftBooks.find(d => d._id === manuscriptId);
    const prevCoverId = draft?.linkedCoverId || null;
    try {
      const res = await fetch(`${API_BASE}/api/drafts/${manuscriptId}/link`, {
        method: 'PATCH',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ linkedCoverId: null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || `Failed to unlink (${res.status})`);
        return;
      }
      setDraftBooks(prev => prev.map(d =>
        d._id !== manuscriptId ? d : { ...d, linkedCoverId: null, linkedCoverTitle: null }
      ));
      if (prevCoverId) {
        setCoverDrafts(prev => prev.map(c =>
          c._id !== prevCoverId ? c : { ...c, linkedManuscriptId: null, linkedManuscriptTitle: null }
        ));
      }
    } catch (err) {
      console.error('Unlink failed:', err);
      alert('Could not unlink. Please try again.');
    }
  };

  const handleUnlinkManuscript = async (coverId) => {
    if (!accessToken) return;
    const cover = coverDrafts.find(d => d._id === coverId);
    const prevMsId = cover?.linkedManuscriptId || null;
    try {
      const res = await fetch(`${API_BASE}/api/drafts/${coverId}/link`, {
        method: 'PATCH',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ linkedManuscriptId: null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || `Failed to unlink (${res.status})`);
        return;
      }
      setCoverDrafts(prev => prev.map(c =>
        c._id !== coverId ? c : { ...c, linkedManuscriptId: null, linkedManuscriptTitle: null }
      ));
      if (prevMsId) {
        setDraftBooks(prev => prev.map(d =>
          d._id !== prevMsId ? d : { ...d, linkedCoverId: null, linkedCoverTitle: null }
        ));
      }
    } catch (err) {
      console.error('Unlink failed:', err);
      alert('Could not unlink. Please try again.');
    }
  };

  const handleRemoveFromWishlist = async (bookId) => {
    if (!accessToken) return;

    try {
      const res = await fetch(`${API_BASE}/api/wishlist/${bookId}`, {
        method: 'DELETE',
        credentials: 'omit',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (res.ok) {
        setWishlistBooks(prev => prev.filter(b => b._id !== bookId));
      } else {
        alert('Failed to remove from wishlist');
      }
    } catch (error) {
      alert('Could not remove from wishlist. Please try again.');
    }
  };

  const cssVars = {
    '--rv-topbar-height': `${sizes.top}px`,
    '--rv-footer-height': `${sizes.bottom}px`,
    '--rv-sidebar-width': '240px',
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      minHeight: 'calc(100vh - var(--rv-topbar-height) - var(--rv-footer-height))',
      margin: 0,
      padding: 0,
      ...cssVars
    }}>
      <SidebarLibrary
        onMyBooksClick={() => switchView('myBooks')}
        onDraftsClick={() => switchView('drafts')}
        onExploreNewBooksClick={() => switchView('library')}
        onPurchasedBooksClick={() => switchView('purchased')}
        onCoverMarketClick={() => switchView('coverMarket')}
        onMyCoversClick={() => switchView('myCovers')}
        onWishlistClick={() => switchView('wishlist')}
        onTemplateGalleryClick={() => switchView('templates')}
        activeView={viewMode}
        rejectedDraftsCount={rejectedDraftsCount}
      />
      <Box sx={{
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        px: 4,
        py: 3,
        // The Manuscripts tab gets a tinted ground. The prototype floats its
        // panels over a photograph; the panels are what carry the design, and
        // on a white page they had nothing to lift off -- white cards inside
        // white sections inside a white page read as one flat sheet. purpleTint
        // is the lightest surface in the scale and picks up the purple rail
        // without competing with it. `alignSelf` because the parent is
        // `flex-start`, so without it the colour stops where the content does.
        ...(viewMode === 'drafts' || viewMode === 'templates'
          ? { bgcolor: tokens.color.purpleTint, alignSelf: 'stretch' }
          : null),
      }}>
        {/* The Manuscripts and Templates tabs carry their own hero headings, per
            their prototypes, so the generic page title would be a duplicate. */}
        <Typography variant="h4" gutterBottom sx={{ display: viewMode === 'drafts' || viewMode === 'templates' ? 'none' : undefined }}>
          {viewMode === 'myBooks' ? 'My Books' :
           viewMode === 'drafts' ? 'Drafts' :
           viewMode === 'purchased' ? 'Purchased Books' :
           viewMode === 'wishlist' ? 'Wishlist' :
           viewMode === 'coverMarket' ? 'Cover Market' :
           viewMode === 'myCovers' ? 'My Covers' :
           'Library'}
        </Typography>

        <Collapse in={!!error}>
          <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>
        </Collapse>

        {viewMode === 'templates' ? (
          // The Template Gallery, as a tab like the others rather than a page
          // of its own. It loads its own catalogue.
          <TemplateGallery />
        ) : viewMode === 'library' ? (
          // Public Library View
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search by title, author..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 280 }}
              />
              <Typography variant="subtitle2" sx={{ ml: 1 }}>Filter by:</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {genres.map(g => (
                  <Chip
                    key={g}
                    label={g}
                    clickable
                    size="small"
                    color={g === activeGenre ? 'secondary' : 'default'}
                    onClick={() => setActiveGenre(g)}
                  />
                ))}
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                <CircularProgress />
              </Box>
            ) : filtered.length === 0 ? (
              <Typography variant="body2" sx={{ mt: 4, opacity: 0.7 }}>
                No books yet.
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {filtered.map((book) => (
                  <Grid item key={book._id || book.id} xs={12} sm={6} md={3}>
                    <BookCard book={book} onCardClick={setModalBook} />
                  </Grid>
                ))}
              </Grid>
            )}

            <BookCardModal
              open={!!modalBook}
              onClose={() => setModalBook(null)}
              context="library"
              book={modalBook}
              onOpenReader={handleOpenReader}
              onBuy={handleBuy}
            />
          </>
        ) : viewMode === 'purchased' ? (
          // Purchased Books View
          <>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search your books..."
                value={purchasedSearch}
                onChange={(e) => setPurchasedSearch(e.target.value)}
                sx={{ minWidth: 280 }}
              />
              <Typography variant="subtitle2" sx={{ ml: 1 }}>Filter by:</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {purchasedStatuses.map(s => (
                  <Chip
                    key={s}
                    label={s}
                    clickable
                    size="small"
                    color={s === purchasedStatus ? 'secondary' : 'default'}
                    onClick={() => setPurchasedStatus(s)}
                  />
                ))}
              </Box>
            </Box>
            {loading ? (
              <CircularProgress />
            ) : ownedBooks.length === 0 ? (
              <Typography variant="body2">You haven't purchased any books yet.</Typography>
            ) : (() => {
              const term = purchasedSearch.trim().toLowerCase();
              const visibleOwned = ownedBooks.filter(b => {
                const libBook = mapToLibraryBook(b, user);
                const matchSearch = !term ||
                  (libBook.title || '').toLowerCase().includes(term) ||
                  (libBook.authorName || '').toLowerCase().includes(term);
                const matchStatus = purchasedStatus === 'All' || (b.status || 'Unread') === purchasedStatus;
                return matchSearch && matchStatus;
              });
              return visibleOwned.length === 0 ? (
                <Typography variant="body2">No books match your search.</Typography>
              ) : (
                <Grid container spacing={2}>
                  {visibleOwned.map(b => {
                    const libBook = mapToLibraryBook(b, user);
                    return (
                      <Grid item key={libBook._id} xs={12} sm={6} md={3}>
                        <BookCard book={libBook} showProgress />
                      </Grid>
                    );
                  })}
                </Grid>
              );
            })()}
          </>
        ) : viewMode === 'wishlist' ? (
          // Wishlist View
          <>
            {loading ? (
              <CircularProgress />
            ) : wishlistBooks.length === 0 ? (
              <Typography variant="body2">Your wishlist is empty.</Typography>
            ) : (
              <Grid container spacing={2}>
                {wishlistBooks.map(b => {
                  const libBook = mapToLibraryBook(b, user);
                  return (
                    <Grid item key={libBook._id} xs={12} sm={6} md={3}>
                      <Box sx={{ position: 'relative' }}>
                        <BookCard book={libBook} onCardClick={setModalBook} />
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            borderRadius: '50%',
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: '#ffebee',
                            }
                          }}
                          onClick={() => handleRemoveFromWishlist(libBook._id)}
                          title="Remove from wishlist"
                        >
                          ×
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </>
        ) : viewMode === 'coverMarket' ? (
          // Cover Marketplace View
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search by title, designer..."
                value={coverSearch}
                onChange={(e) => setCoverSearch(e.target.value)}
                sx={{ minWidth: 280 }}
              />
              <Typography variant="subtitle2" sx={{ ml: 1 }}>Filter by:</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {coverTypes.map(t => (
                  <Chip
                    key={t}
                    label={t}
                    clickable
                    size="small"
                    color={t === activeCoverType ? 'secondary' : 'default'}
                    onClick={() => setActiveCoverType(t)}
                  />
                ))}
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                <CircularProgress />
              </Box>
            ) : filteredCovers.length === 0 ? (
              <Typography variant="body2" sx={{ mt: 4, opacity: 0.7 }}>
                No covers available yet.
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {filteredCovers.map((cover) => (
                  <Grid item key={cover._id || cover.id} xs={12} sm={6} md={3}>
                    <CoverCard cover={cover} onCardClick={setModalCover} />
                  </Grid>
                ))}
              </Grid>
            )}

            <CoverCardModal
              open={!!modalCover}
              onClose={() => setModalCover(null)}
              context="coverMarket"
              cover={modalCover}
            />
          </>
        ) : viewMode === 'myCovers' ? (
          // My Covers Dashboard — 4 conditional sections
          <>
            {loading ? (
              <CircularProgress />
            ) : (
              <>
                {/* My Covers (cover-only drafts) */}
                {coverDrafts.length > 0 && (
                  <>
                    <Typography variant="h5" sx={{ mt: 3, mb: 2 }}>
                      🎨 My Covers
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                      {coverDrafts.map(d => (
                        <Grid item key={d._id} xs={12} sm={6} md={3}>
                          <EditorCoverCard
                            cover={{
                              id: d._id,
                              title: d.title || 'Untitled Cover',
                              genre: d.genre,
                              volume: d.volume,
                              coverImage: d.coverImage,
                              cover_url: d.cover_url,
                              coverVersions: d.coverVersions || [],
                              activeCoverVersionId: d.activeCoverVersionId,
                              linkedManuscriptTitle: d.linkedManuscriptTitle || null,
                            }}
                            onClick={() => navigate(`/editor/${d._id}/cover`)}
                            onDelete={() => handleDeleteCoverDraft(d._id)}
                            onLinkManuscript={() => handleOpenLinkManuscript(d._id)}
                            onUnlinkManuscript={d.linkedManuscriptId ? () => handleUnlinkManuscript(d._id) : undefined}
                            onOpenStructure={() => navigate(`/bookcover/${d._id}/structure`)}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}

                {/* Assigned to Me (covers where user is a collaborator) */}
                {assignedCovers.length > 0 && (
                  <>
                    <Typography variant="h5" sx={{ mt: 3, mb: 2 }}>
                      🤝 Assigned to Me
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                      {assignedCovers.map(task => (
                        <Grid item key={`${task._id}-${task.versionId || ''}`} xs={12} sm={6} md={3}>
                          <EditorCoverCard
                            cover={{
                              id: task._id,
                              title: task.title,
                              genre: task.genre,
                              volume: task.volume,
                              cover_url: task.cover_url,
                              coverVersions: task.versionId
                                ? [{ id: task.versionId, coverStatus: task.coverStatus, coverType: null }]
                                : [],
                              activeCoverVersionId: task.versionId,
                            }}
                            onClick={() => handleOpenAssignedCover(task)}
                            onDelete={null}
                            versionName={task.versionName}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}

                {/* Pending Review */}
                {pendingCovers.length > 0 && (
                  <>
                    <Typography variant="h5" sx={{ mt: 3, mb: 2 }}>
                      ⏳ Pending Review
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                      {pendingCovers.map(d => (
                        <Grid item key={d._id} xs={12} sm={6} md={3}>
                          <DashboardCard
                            title={d.title}
                            genre={d.genre}
                            volume={d.volume}
                            status="pending_review"
                            role="author"
                            openTo={`/editor/${d._id}/cover`}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}

                {/* Listed in Market */}
                {listedCovers.length > 0 && (
                  <>
                    <Typography variant="h5" sx={{ mt: 3, mb: 2 }}>
                      🛒 Listed in Cover Market
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                      {listedCovers.map(cover => (
                        <Grid item key={cover._id || cover.id} xs={12} sm={6} md={3}>
                          <CoverCard cover={cover} onCardClick={setModalCover} />
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}

                {/* Purchased Covers */}
                {myCovers.length > 0 && (
                  <>
                    <Typography variant="h5" sx={{ mt: 3, mb: 2 }}>
                      📦 Purchased Covers
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                      {myCovers.map(cover => (
                        <Grid item key={cover._id || cover.id} xs={12} sm={6} md={3}>
                          <CoverCard cover={cover} onCardClick={setModalCover} />
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}

                {/* Manuscript Covers */}
                {manuscriptCovers.length > 0 && (
                  <>
                    <Typography variant="h5" sx={{ mt: 3, mb: 2 }}>
                      📄 Manuscript Covers
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                      {manuscriptCovers.map(d => (
                        <Grid item key={d._id} xs={12} sm={6} md={3}>
                          <EditorCoverCard
                            cover={{
                              id: d._id,
                              title: d.title || 'Untitled',
                              genre: d.genre,
                              volume: d.volume,
                              coverImage: d.coverImage,
                              cover_url: d.cover_url,
                              coverVersions: d.coverVersions || [],
                              activeCoverVersionId: d.activeCoverVersionId,
                              linkedManuscriptTitle: d.title || 'Manuscript',
                            }}
                            onClick={() => navigate(`/editor/${d._id}/cover`)}
                            onDelete={() => handleDeleteManuscriptCover(d._id)}
                            // These cards are manuscripts that hold their own cover, not
                            // standalone covers — the book has an inside, so the title page
                            // and colophon really do print in it. The page therefore keeps
                            // both halves; `?element=` just opens it on the cover one,
                            // which is the half the author clicked a cover to reach.
                            onOpenStructure={() => navigate(`/manuscript/${d._id}/structure?element=title-page`)}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}

                {/* Empty state */}
                {coverDrafts.length === 0 && assignedCovers.length === 0 && pendingCovers.length === 0 && listedCovers.length === 0 && myCovers.length === 0 && manuscriptCovers.length === 0 && (
                  <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
                    You don't have any covers yet. Create one in the Editor or browse the Cover Market!
                  </Typography>
                )}
              </>
            )}

            <CoverCardModal
              open={!!modalCover}
              onClose={() => setModalCover(null)}
              context="myCovers"
              cover={modalCover}
            />
          </>
        ) : viewMode === 'drafts' ? (
          // Manuscripts View. Rebuilt to the prototype attached to the ticket: a
          // wrapping card grid per section plus a calendar rail, replacing the
          // three horizontally-scrolling DocumentCard rows. A card now opens an
          // in-tab detail view instead of jumping straight to the editor.
          <>
            {loading ? (
              <CircularProgress />
            ) : (
              <ManuscriptsWorkspace
                manuscripts={draftBooks}
                currentUserId={currentUserId}
                search={myBooksSearch}
                onSearchChange={setMyBooksSearch}
                onOpenEditor={(id) => navigate(`/editor/${id}`)}
                onOpenStructure={(id) => navigate(`/manuscript/${id}/structure`)}
                onOpenPricing={(id) => navigate(`/manuscript/${id}/pricing`)}
                onDeleteDraft={handleDeleteDraft}
                onLinkCover={handleOpenLinkCover}
                onUnlinkCover={handleUnlinkCover}
                onManuscriptRemoved={handleManuscriptRemoved}
              />
            )}
          </>
        ) : (
          // My Books View
          <>
            {loading ? (
              <CircularProgress />
            ) : (
              <>
                {/* Search + Filter bar */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder="Search your books..."
                    value={myBooksSearch}
                    onChange={e => setMyBooksSearch(e.target.value)}
                    sx={{ minWidth: 280 }}
                  />
                  <Typography variant="subtitle2" sx={{ ml: 1 }}>Filter by:</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {MY_BOOKS_FILTERS.map(f => (
                      <Chip
                        key={f}
                        label={f}
                        clickable
                        size="small"
                        color={myBooksFilter === f ? 'secondary' : 'default'}
                        onClick={() => setMyBooksFilter(f)}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Approved Books section */}
                {(myBooksFilter === 'All' || myBooksFilter === 'Approved') && (() => {
                  const items = approvedBooks.filter(b =>
                    !myBooksSearch || b.title?.toLowerCase().includes(myBooksSearch.toLowerCase())
                  );
                  if (!items.length) return null;
                  return (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 600 }}>
                        ✅ Approved (Ready to Publish)
                      </Typography>
                      <Box className="scroll-wrapper">
                        <IconButton className="scroll-arrow scroll-arrow-left" onClick={() => scrollContainer(approvedScrollRef, 'left')}>
                          <ChevronLeftIcon />
                        </IconButton>
                        <Box className="scroll-container" ref={approvedScrollRef}>
                          {items.map(book => (
                            <DashboardCard
                              key={book._id}
                              title={book.title} genre={book.genre} volume={book.volume}
                              status="approved" role="author"
                              openTo={`/read/${book._id}?type=draft`}
                              openState={{ book: { _id: book._id, title: book.title, pdfUrl: computePdfUrl(book, book.status) } }}
                              onApprove={() => handleFinalize(book._id)}
                            />
                          ))}
                        </Box>
                        <IconButton className="scroll-arrow scroll-arrow-right" onClick={() => scrollContainer(approvedScrollRef, 'right')}>
                          <ChevronRightIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })()}

                {/* Pending Review section */}
                {(myBooksFilter === 'All' || myBooksFilter === 'Pending') && (() => {
                  const items = pendingBooks.filter(b =>
                    !myBooksSearch || b.title?.toLowerCase().includes(myBooksSearch.toLowerCase())
                  );
                  if (!items.length) return null;
                  return (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 600 }}>
                        ⏳ Pending Review
                      </Typography>
                      <Box className="scroll-wrapper">
                        <IconButton className="scroll-arrow scroll-arrow-left" onClick={() => scrollContainer(pendingScrollRef, 'left')}>
                          <ChevronLeftIcon />
                        </IconButton>
                        <Box className="scroll-container" ref={pendingScrollRef}>
                          {items.map(book => (
                            <DashboardCard
                              key={book._id}
                              title={book.title} genre={book.genre} volume={book.volume}
                              status="pending_review" role="author"
                              openTo={`/read/${book._id}?type=draft`}
                              openState={{ book: { _id: book._id, title: book.title, pdfUrl: computePdfUrl(book, book.status) } }}
                            />
                          ))}
                        </Box>
                        <IconButton className="scroll-arrow scroll-arrow-right" onClick={() => scrollContainer(pendingScrollRef, 'right')}>
                          <ChevronRightIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })()}

                {/* Published Books section */}
                {(myBooksFilter === 'All' || myBooksFilter === 'Published') && (() => {
                  const items = publishedBooks.filter(b =>
                    !myBooksSearch || b.title?.toLowerCase().includes(myBooksSearch.toLowerCase())
                  );
                  if (!items.length) return null;
                  return (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 600 }}>
                        📖 Published
                      </Typography>
                      <Box className="scroll-wrapper">
                        <IconButton className="scroll-arrow scroll-arrow-left" onClick={() => scrollContainer(publishedScrollRef, 'left')}>
                          <ChevronLeftIcon />
                        </IconButton>
                        <Box className="scroll-container" ref={publishedScrollRef}>
                          {items.map(pub => {
                            const libBook = mapToLibraryBook(pub, user);
                            return <BookCard key={libBook._id} book={libBook} />;
                          })}
                        </Box>
                        <IconButton className="scroll-arrow scroll-arrow-right" onClick={() => scrollContainer(publishedScrollRef, 'right')}>
                          <ChevronRightIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })()}

                {/* Empty state when nothing at all */}
                {approvedBooks.length === 0 && pendingBooks.length === 0 && publishedBooks.length === 0 && (
                  <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
                    You don't have any books yet. Write a draft in the Editor and submit it for review!
                  </Typography>
                )}
              </>
            )}
          </>
        )}
      </Box>

      <LinkModal
        open={linkModal.open}
        onClose={() => setLinkModal(s => ({ ...s, open: false }))}
        mode={linkModal.mode}
        currentLinkedId={linkModal.currentLinkedId}
        onConfirm={handleLinkConfirm}
      />

      <Dialog
        open={deleteConfirm.open}
        onClose={closeDeleteConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {deleteConfirm.scenario === 'manuscript_cover' && 'Delete Cover'}
          {deleteConfirm.scenario === 'draft_with_linked_cover' && 'Delete Manuscript'}
          {deleteConfirm.scenario === 'draft_no_cover' && 'Delete Manuscript'}
          {deleteConfirm.scenario === 'cover_only_draft' && 'Delete Cover'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5, p: 1.5, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffc107' }}>
            <Typography variant="body2" sx={{ color: '#856404', fontWeight: 600 }}>
              ⚠️ Warning: This action cannot be undone.
            </Typography>
          </Box>

          {deleteConfirm.scenario === 'manuscript_cover' && (
            <Typography variant="body2">
              What would you like to delete? Your manuscript content will remain safe if you choose to delete the cover only.
            </Typography>
          )}
          {deleteConfirm.scenario === 'draft_with_linked_cover' && (
            <Typography variant="body2">
              This manuscript has a linked cover. You can delete the manuscript only, or delete both the manuscript and its linked cover.
            </Typography>
          )}
          {(deleteConfirm.scenario === 'draft_no_cover' || deleteConfirm.scenario === 'cover_only_draft') && (
            <Typography variant="body2">
              Are you sure you want to permanently delete this {deleteConfirm.scenario === 'cover_only_draft' ? 'cover' : 'manuscript'}?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 2.5, alignItems: 'stretch' }}>
          {deleteConfirm.scenario === 'manuscript_cover' && (
            <>
              <Button onClick={handleConfirmCoverOnly} variant="outlined" color="error" fullWidth>
                Delete Cover Only
              </Button>
              <Button onClick={handleConfirmCoverAndManuscript} variant="contained" color="error" fullWidth>
                Delete Cover &amp; Manuscript
              </Button>
            </>
          )}
          {deleteConfirm.scenario === 'draft_with_linked_cover' && (
            <>
              <Button onClick={handleConfirmManuscriptOnly} variant="outlined" color="error" fullWidth>
                Delete Manuscript Only
              </Button>
              <Button onClick={handleConfirmManuscriptAndLinkedCover} variant="contained" color="error" fullWidth>
                Delete Manuscript &amp; Cover
              </Button>
            </>
          )}
          {deleteConfirm.scenario === 'draft_no_cover' && (
            <Button onClick={handleConfirmManuscriptOnly} variant="contained" color="error" fullWidth>
              Delete Manuscript
            </Button>
          )}
          {deleteConfirm.scenario === 'cover_only_draft' && (
            <Button onClick={handleConfirmCoverDraft} variant="contained" color="error" fullWidth>
              Delete Cover
            </Button>
          )}
          <Button onClick={closeDeleteConfirm} variant="text" fullWidth>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
